import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ragService from '../../services/rag.service';
import { crmAPI } from '../../services/api';

const CRMSync = () => {
  const [collectionId, setCollectionId] = useState('');
  const [collections, setCollections] = useState([]);
  const [crmConfig, setCrmConfig] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [syncOptions, setSyncOptions] = useState({
    customers: true,
    products: true,
    orders: false,
    tickets: true,
    conversations: false
  });
  const [syncProgress, setSyncProgress] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [collectionsRes, crmConfigRes] = await Promise.all([
        ragService.getCollections(),
        crmAPI.getConfig()
      ]);
      
      setCollections(collectionsRes.collections || []);
      setCrmConfig(crmConfigRes.data?.config || {});
      // Do NOT auto-select — user must explicitly choose a collection
    } catch (err) {
      console.error('Failed to load data', err);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async (e) => {
    e.preventDefault();

    if (!collectionId) {
      setError('Please select a collection');
      return;
    }

    const activeCRM = crmConfig?.active_crm;
    if (!activeCRM) {
      setError('No active CRM configured. Please configure a CRM first.');
      return;
    }

    const selectedDataTypes = Object.entries(syncOptions)
      .filter(([_, enabled]) => enabled)
      .map(([type]) => type);

    if (selectedDataTypes.length === 0) {
      setError('Please select at least one data type to sync');
      return;
    }

    setSyncing(true);
    setError('');
    setSuccessMessage('');
    setSyncProgress({ status: 'Starting sync...', progress: 0 });

    try {
      // Call backend to sync CRM data to RAG - collection_id is now UUID string
      const response = await ragService.syncCRMData({
        collection_id: collectionId,
        crm_type: activeCRM,
        data_types: selectedDataTypes
      });

      setSyncProgress({ status: 'Completed', progress: 100 });
      setSuccessMessage(
        `Successfully synced ${response.documents_created || 0} documents from ${activeCRM} to collection. ` +
        `${response.chunks_created || 0} chunks created.`
      );
      
    } catch (err) {
      console.error('Sync failed', err);
      const errorMsg = err.response?.data?.detail || err.message || 'Sync failed';
      setError(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg));
    } finally {
      setSyncing(false);
      setSyncProgress(null);
    }
  };

  const getCRMDisplayName = (crmType) => {
    const names = {
      salesforce: 'Salesforce',
      hubspot: 'HubSpot',
      zoho: 'Zoho CRM',
      dynamics365: 'Dynamics 365',
      genesys_cloud: 'Genesys Cloud',
      sprinklr: 'Sprinklr',
      custom: 'Custom CRM'
    };
    return names[crmType] || crmType;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (collections.length === 0) {
    return (
      <div className="p-6 space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">CRM to RAG Sync</h1>
          <p className="mt-1 text-sm text-gray-600">Sync CRM data to your vector database</p>
        </div>

        <div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg p-8 text-center">
          <div className="text-6xl mb-4">⚠️</div>
          <h2 className="text-xl font-bold text-yellow-900 mb-2">No Collections Found</h2>
          <p className="text-yellow-800 mb-6">
            You need to create a collection before syncing CRM data.
          </p>
          <button
            onClick={() => navigate('/rag/collections')}
            className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            Create Collection
          </button>
        </div>
      </div>
    );
  }

  const activeCRM = crmConfig?.active_crm;
  const activeCRMConfig = crmConfig?.[activeCRM];

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">CRM to RAG Sync</h1>
        <p className="mt-1 text-sm text-gray-600">
          Sync customer data, products, and tickets from your CRM to the vector database
        </p>
      </div>

      {/* CRM Status Card */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">CRM Connection Status</h2>
        {activeCRM ? (
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className={`w-3 h-3 rounded-full ${activeCRMConfig?.enabled ? 'bg-green-500' : 'bg-gray-400'}`}></div>
              <div>
                <p className="font-medium text-gray-900">{getCRMDisplayName(activeCRM)}</p>
                <p className="text-sm text-gray-500">
                  {activeCRMConfig?.enabled ? 'Connected and enabled' : 'Not enabled'}
                </p>
              </div>
            </div>
            <button
              onClick={() => navigate('/crm-config')}
              className="px-4 py-2 text-sm text-indigo-600 hover:text-indigo-700 border border-indigo-300 rounded-lg hover:bg-indigo-50"
            >
              Configure CRM
            </button>
          </div>
        ) : (
          <div className="text-center py-4">
            <p className="text-gray-500 mb-4">No CRM configured</p>
            <button
              onClick={() => navigate('/crm-config')}
              className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
            >
              Configure CRM
            </button>
          </div>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative">
          <span className="block sm:inline">{error}</span>
          <button onClick={() => setError('')} className="absolute top-0 bottom-0 right-0 px-4 py-3">
            <span className="text-2xl">&times;</span>
          </button>
        </div>
      )}

      {/* Success Message */}
      {successMessage && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative">
          <span className="block sm:inline">{successMessage}</span>
          <button onClick={() => setSuccessMessage('')} className="absolute top-0 bottom-0 right-0 px-4 py-3">
            <span className="text-2xl">&times;</span>
          </button>
        </div>
      )}

      {/* Sync Form */}
      {activeCRM && activeCRMConfig?.enabled && (
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
          <form onSubmit={handleSync} className="space-y-6">
            {/* Target Collection — explicit selection required */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                🎯 Target Collection <span className="text-red-500">*</span>
              </label>
              <p className="text-xs text-gray-500 mb-3">
                Select the collection where CRM data will be embedded and stored.
              </p>

              {!collectionId && (
                <div className="mb-3 p-3 bg-amber-50 border border-amber-300 rounded-lg flex items-start gap-2">
                  <span className="text-amber-500 text-lg">⚠️</span>
                  <p className="text-sm text-amber-700 font-medium">
                    You must select a target collection before syncing.
                  </p>
                </div>
              )}

              <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
                {collections.length === 0 ? (
                  <p className="p-4 text-sm text-gray-400 text-center">No collections available</p>
                ) : (
                  collections.map((collection) => {
                    const optionId = String(collection.id);
                    const optionName = collection.name || collection.collection_name;
                    const isSelected = collectionId === optionId;
                    return (
                      <label
                        key={collection.id}
                        className={`flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors ${
                          isSelected
                            ? 'bg-indigo-50 border-l-4 border-indigo-500'
                            : 'hover:bg-gray-50 border-l-4 border-transparent'
                        }`}
                      >
                        <input
                          type="radio"
                          name="targetCollection"
                          value={optionId}
                          checked={isSelected}
                          onChange={() => setCollectionId(optionId)}
                          className="h-4 w-4 text-indigo-600 focus:ring-indigo-500"
                        />
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-medium truncate ${isSelected ? 'text-indigo-700' : 'text-gray-700'}`}>
                            {optionName}
                          </p>
                          <p className="text-xs text-gray-400">{collection.documents_count || 0} docs</p>
                        </div>
                        {isSelected && <span className="text-indigo-500 text-lg">✅</span>}
                      </label>
                    );
                  })
                )}
              </div>
            </div>

            {/* Data Types Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Data Types to Sync
              </label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {[
                  { key: 'customers', label: 'Customers', icon: '👥', description: 'Customer profiles and contact info' },
                  { key: 'products', label: 'Products', icon: '📦', description: 'Product catalog and details' },
                  { key: 'orders', label: 'Orders', icon: '🛒', description: 'Order history and transactions' },
                  { key: 'tickets', label: 'Tickets', icon: '🎫', description: 'Support tickets and issues' },
                  { key: 'conversations', label: 'Conversations', icon: '💬', description: 'Chat and call transcripts' }
                ].map(({ key, label, icon, description }) => (
                  <label
                    key={key}
                    className={`flex items-start p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      syncOptions[key]
                        ? 'border-indigo-500 bg-indigo-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={syncOptions[key]}
                      onChange={(e) => setSyncOptions(prev => ({ ...prev, [key]: e.target.checked }))}
                      className="mt-1 h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                    />
                    <div className="ml-3">
                      <span className="text-lg mr-2">{icon}</span>
                      <span className="font-medium text-gray-900">{label}</span>
                      <p className="text-xs text-gray-500 mt-1">{description}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Sync Progress */}
            {syncProgress && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-blue-800">{syncProgress.status}</span>
                  <span className="text-sm text-blue-600">{syncProgress.progress}%</span>
                </div>
                <div className="w-full bg-blue-200 rounded-full h-2">
                  <div
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${syncProgress.progress}%` }}
                  ></div>
                </div>
              </div>
            )}

            {/* Sync Button */}
            <button
              type="submit"
              disabled={syncing || !collectionId}
              className="w-full px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center justify-center space-x-2"
            >
              {syncing ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  <span>Syncing...</span>
                </>
              ) : (
                <>
                  <span>🔄</span>
                  <span>Sync CRM Data to RAG</span>
                </>
              )}
            </button>
          </form>
        </div>
      )}

      {/* Instructions */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h3 className="text-sm font-semibold text-blue-900 mb-2">📝 CRM Sync Instructions</h3>
        <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
          <li>Ensure your CRM is configured and connected in the CRM Integration settings</li>
          <li>Select a target collection where CRM data will be indexed</li>
          <li>Choose which data types you want to sync (customers, products, tickets, etc.)</li>
          <li>The data will be converted to documents and indexed for semantic search</li>
          <li>Syncing may take several minutes depending on the amount of data</li>
        </ul>
      </div>
    </div>
  );
};

export default CRMSync;
